import { Await, data, LoaderFunctionArgs } from "react-router";
import { useLoaderData, useRouteError, isRouteErrorResponse } from "react-router";
import { CloudAlertIcon, MapPinHouse } from "lucide-react";
import { getPublicVenueBySlugId, getVenueFutureSessions } from "~/models/Venue";
import { Suspense } from "react";
import Loading from "~/components/Loading";
import VerticalCardList from "~/components/VerticalCardList";
import PlayCard from "~/components/PlayCard";
import NoResults from "./components/NoResults";
import { groupSessionsByRelativeDate } from "~/models/Session";
import SupportEmail from "~/components/SupportEmail";
import { playPath } from "~/services/url-builder";

export async function loader({ request, params }: LoaderFunctionArgs) {
  const { path } = params;

  if (!path) {
    return null;
  }

  const slugParms = path.split("-");
  const slugId = slugParms[slugParms.length - 1];

  const venue = await getPublicVenueBySlugId(slugId);

  if (!venue) {
    throw data("Record Not Found", { status: 404 });
  }

  const sessionsDays = getVenueFutureSessions(venue.id).then((sessions) => groupSessionsByRelativeDate(sessions));

  return { venue, sessionsDays };
}

export default function VenueDetailsPage() {
  const { venue, sessionsDays } = useLoaderData<typeof loader>()!;

  return (
    <div className="bg-gray-950 pb-10">
      <div
        className="relative isolate overflow-hidden bg-gray-950"
        style={{
          backgroundImage: `
      radial-gradient(circle at center, rgba(17, 24, 39, 0.9), rgba(17, 24, 39, 1)),
      repeating-linear-gradient(45deg, rgba(255,255,255,0.05) 0px, rgba(255,255,255,0.05) 1px, transparent 1px, transparent 10px)
    `,
        }}
      >
        <div className="absolute inset-0 bg-linear-to-b from-red-700/50 to-gray-950/90" />

        {/* Decorative elements */}
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage: `radial-gradient(circle at 25% 25%, rgba(255,255,255,0.1) 1px, transparent 1px)`,
            backgroundSize: "24px 24px",
          }}
        />

        {/* Content */}
        <div className="relative flex flex-col gap-8 items-center justify-center text-center min-h-96 px-4 py-20">
          <h2 className="text-4xl font-bold text-white/90">{venue.city}</h2>
          <h1 className="text-6xl font-bold text-white">{venue.name}</h1>
        </div>
      </div>

      <div className="p-4 bg-white shadow-xs rounded-lg overflow-hidden max-w-7xl mx-auto py-6 px-2 sm:px-6 lg:px-8">
        <Suspense fallback={<Loading />}>
          <Await resolve={sessionsDays} errorElement={<SessionsListError />}>
            {(sessionsDays) => <SessionsList sessionsDays={sessionsDays} />}
          </Await>
        </Suspense>
      </div>
    </div>
  );
}

function SessionsList({ sessionsDays }) {
  if (!sessionsDays || !sessionsDays.length) {
    return <NoResults />;
  }

  return (
    <div>
      {sessionsDays.map((sessionsDay) => (
        <div key={sessionsDay.date} className=" flex flex-row flex-wrap gap-2 mt-0  mb-16">
          <h3 className="font-semibold text-2xl mb-4 border-b pb-4 w-full">Espetáculos para {sessionsDay.date}</h3>
          <VerticalCardList>
            {sessionsDay.sessions.map((session) => (
              <PlayCard
                key={session.id}
                href={playPath(session.play)}
                title={session.play.title}
                imageUrl={session.play.images?.[0].publicUrl}
                datetime={session.datetime}
              />
            ))}
          </VerticalCardList>
        </div>
      ))}
    </div>
  );
}

function SessionsListError() {
  return (
    <div className="container max-w-2xl mx-auto bg-white px-4 py-4 mt-0 mb-20 flex-1 text-center">
      <div className="max-w-xl min-h-60 mx-auto bg-gray-100 p-4 pb-10 flex-1 rounded border flex items-center justify-center flex-col gap-4 text-gray-700">
        <h1 className="font-bold text-2xl text-gray-500 mt-6 mb-4">
          <CloudAlertIcon size={128} strokeWidth={1.5} className="text-gray-400" />
        </h1>
        <p>Ocorreu um erro ao carregar as sessões. Por favor, tente novamente mais tarde.</p>
        <p>
          Se o erro continuar, por favor contacte-nos através do email <SupportEmail />.
        </p>
      </div>
    </div>
  );
}

// export function ErrorBoundary() {
//   const error = useRouteError();
//   console.log("Venue Details Error:", error);

//   if (isRouteErrorResponse(error)) {
//     return (
//       <div className="p-10 bg-red-50 border border-red-200 rounded">
//         <h1 className="text-2xl font-bold text-red-700">Error {error.status}</h1>
//         <p className="mt-2">{error.data || error.statusText || "An error occurred"}</p>
//       </div>
//     );
//   }

//   return (
//     <div className="p-10 bg-red-50 border border-red-200 rounded">
//       <h1 className="text-2xl font-bold text-red-700">Unexpected Error</h1>
//       <p className="mt-2">Something went wrong loading the venue.</p>
//     </div>
//   );
// }
